import { html, LitElement } from "lit"
import { customElement, property } from "lit/decorators.js"
import mapboxgl from "mapbox-gl"
import { MapControlHost } from "./map-control-host.ts"
import { matchesTripFilter, type TripFilter } from "../trips/filter.ts"
import { ensureMapStylesheet } from "../map/bridge.ts"
import { MAP_EVENT, mapEvent, type MapTrip, parseMapTrips } from "../map/types.ts"
import { applyDepth, type MapStyle, STYLE_STORAGE_KEY, STYLE_URLS } from "./map-styles.ts"

interface MarkerPosition {
  lat: number
  lng: number
}

@customElement("globe-view")
export class GlobeView extends MapControlHost(LitElement) {
  // Disable shadow DOM so Mapbox GL CSS applies directly
  override createRenderRoot() {
    return this
  }

  @property({ attribute: "token" })
  accessor token: string = ""

  @property({ attribute: "trips" })
  accessor tripsJson: string = "[]"

  @property({ attribute: "height" })
  accessor height: string = "500px"

  @property({ attribute: "style-name" })
  accessor styleName: string = "terrain"

  /** Live markers, so a re-render can take the previous set off the map. */
  private _markers: mapboxgl.Marker[] = []

  private _filter: TripFilter | null = null

  private _getInitialStyle(): MapStyle {
    try {
      const stored = localStorage.getItem(STYLE_STORAGE_KEY) as MapStyle | null
      if (stored && stored in STYLE_URLS) return stored
    } catch {
      // localStorage unavailable
    }
    const attr = this.styleName as MapStyle
    return attr in STYLE_URLS ? attr : "terrain"
  }

  override connectedCallback() {
    super.connectedCallback()
    void ensureMapStylesheet()
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this._map?.remove()
    this._map = null
  }

  override firstUpdated() {
    if (!this.token) return

    const container = this.querySelector<HTMLDivElement>(
      ".globe-view-container",
    )
    if (!container) return

    const initialStyle = this._getInitialStyle()

    this._map = new mapboxgl.Map({
      container,
      accessToken: this.token,
      style: STYLE_URLS[initialStyle],
      zoom: 1.5,
      center: [0, 20],
      performanceMetricsCollection: false,
    })

    this._map.addControl(new mapboxgl.NavigationControl(), "top-right")

    this._map.on("load", () => {
      if (!this._map) return

      // The globe never runs the Trip Player, so it has no light of its own to
      // track: `day` is the only preset it ever shows.
      applyDepth(this._map, initialStyle, "day")

      this._renderMarkers()
      this._flushAndReady()
    })
  }

  /** Re-add the depth stack after a style change — see `trip-map`'s twin. */
  readdDepth(style: MapStyle): void {
    if (this._map) applyDepth(this._map, style, "day")
  }

  /**
   * Replace the visible markers with the trips passing the current filter.
   *
   * Re-entrant: every call tears the previous marker set off the map first.
   * Markers are plain `mapboxgl.Marker`s rather than a GeoJSON layer, so there
   * is no `setFilter` on a source to lean on — the set is rebuilt by hand.
   */
  private _renderMarkers() {
    if (!this._map) return

    for (const marker of this._markers) marker.remove()
    this._markers = []

    const trips = this._parseTrips().filter((t) => matchesTripFilter(t, this._filter))
    const bounds = new mapboxgl.LngLatBounds()
    let hasMarkers = false

    for (const trip of trips) {
      const pos = this._computeMarkerPosition(trip)
      if (!pos) continue

      hasMarkers = true
      bounds.extend([pos.lng, pos.lat])

      const el = document.createElement("div")
      el.style.cssText = `
        width: 28px; height: 28px; border-radius: 50%;
        background: #3b82f6; border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        font-size: 14px;
      `
      el.textContent = "🗺"

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([pos.lng, pos.lat])
        .addTo(this._map!)
      this._markers.push(marker)

      el.addEventListener("click", () => {
        this.dispatchEvent(
          mapEvent(MAP_EVENT.tripFocused, {
            id: trip.id,
            lat: pos.lat,
            lng: pos.lng,
          }),
        )
      })
    }

    if (hasMarkers) {
      if (trips.length > 1) {
        this._map.fitBounds(bounds, {
          padding: 80,
          maxZoom: 8,
          duration: 2000,
        })
      } else {
        const center = bounds.getCenter()
        this._map.flyTo({ center, zoom: 6, speed: 0.6 })
      }
    }
  }

  private _computeMarkerPosition(trip: MapTrip): MarkerPosition | null {
    // Route centroid takes precedence
    if (trip.route?.coordinates && trip.route.coordinates.length > 0) {
      const flat: [number, number][] = trip.route.type === "MultiLineString"
        ? (trip.route.coordinates as [number, number][][]).flat()
        : (trip.route.coordinates as [number, number][])
      if (flat.length > 0) {
        const mid = flat[Math.floor(flat.length / 2)]!
        return { lng: mid[0]!, lat: mid[1]! }
      }
    }

    // Fall back to centroid of photo lat/lngs
    if (trip.photo_latlngs && trip.photo_latlngs.length > 0) {
      const sum = trip.photo_latlngs.reduce(
        (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
        { lat: 0, lng: 0 },
      )
      return {
        lat: sum.lat / trip.photo_latlngs.length,
        lng: sum.lng / trip.photo_latlngs.length,
      }
    }

    return null
  }

  /**
   * Apply a client-side filter to the markers. `null` clears it.
   *
   * Imperative rather than an attribute because the caller is a coordinator
   * island holding the element, the same way `flyTo` is reached. Re-fits the
   * camera to whatever still matches, so filtering doubles as a zoom-to.
   */
  setFilter(filter: TripFilter | null): void {
    this._filter = filter
    this._renderMarkers()
  }

  get isMoving(): boolean {
    return this._map?.isMoving() ?? false
  }

  /**
   * Fly the camera; `false` when no flight went — see `MapElement.flyTo`.
   *
   * Accepts `pitch`/`bearing` to satisfy the shared interface, though nothing
   * on the globe asks for them today: the globe frames whole trips, and the
   * cinematic camera is a thing you point at one POI.
   */
  flyTo(
    lat: number,
    lng: number,
    options?: {
      zoom?: number
      speed?: number
      offset?: [number, number]
      pitch?: number
      bearing?: number
    },
  ): boolean {
    if (!this._map || this._map.isMoving()) return false
    this._map.flyTo({
      center: [lng, lat],
      zoom: options?.zoom ?? 14,
      speed: options?.speed ?? 1.2,
      // Omitted, never `undefined` — see the note on `TripMap.flyTo`.
      ...(options?.offset !== undefined ? { offset: options.offset } : {}),
      ...(options?.pitch !== undefined ? { pitch: options.pitch } : {}),
      ...(options?.bearing !== undefined ? { bearing: options.bearing } : {}),
    })
    return true
  }

  /**
   * Refit the camera to every marker still showing — the framing the globe
   * lands on itself. Re-rendering the markers is that framing: the fit is part
   * of `_renderMarkers`, which is also what `setFilter` leans on.
   */
  resetViewport(): void {
    this._renderMarkers()
  }

  private _parseTrips(): MapTrip[] {
    return parseMapTrips(this.tripsJson)
  }

  override render() {
    return html`
      <div
        class="globe-view-container"
        style="width: 100%; height: ${this
          .height}; border-radius: 8px; overflow: hidden;"
      ></div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "globe-view": GlobeView
  }
}
